// Router de la SPA (React Router 7, modo datos; ARCHITECTURE §9, UX §2.3). Cada feature es un chunk propio
// (`lazy`) y la página pública de reservas no carga el shell.
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { Navigate, Outlet, createBrowserRouter, type RouteObject } from 'react-router';
import type { FeatureRoutesModule } from './featureRoutes';
import { RootLayout } from './RootLayout';
import { AuthCard, AuthLayout } from './AuthLayout';
import {
  AppIndexRedirect,
  EntryRedirect,
  OwnerSetupGate,
  RequireLocation,
  RequireSession,
  SectionGate,
  SettingsGate,
} from './guards';
import { BootScreen, NotFoundInApp, NotFoundPage, RouteErrorPage } from './pages';
import type { SectionKey } from '@/lib/permissions';
/** Carga diferida de las rutas de una feature (exige el contrato `FeatureRoutesModule`). */
function feature(load: () => Promise<FeatureRoutesModule>): ComponentType {
  return lazy(load);
}

const Agenda = feature(() => import('@/features/agenda/routes'));
const Sales = feature(() => import('@/features/sales/routes'));
const Clients = feature(() => import('@/features/clients/routes'));
const GiftCards = feature(() => import('@/features/giftcards/routes'));
const Stock = feature(() => import('@/features/stock/routes'));
const Commissions = feature(() => import('@/features/commissions/routes'));
const Finance = feature(() => import('@/features/finance/routes'));
const Dashboard = feature(() => import('@/features/dashboard/routes'));
const Settings = feature(() => import('@/features/settings/routes'));
const Help = feature(() => import('@/features/help/routes'));
const Account = feature(() => import('@/features/account/routes'));
const Onboarding = feature(() => import('@/features/onboarding/routes'));
const Booking = feature(() => import('@/features/booking/routes'));

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const TwoFactorPage = lazy(() => import('@/features/auth/TwoFactorPage'));
const ForgotPage = lazy(() => import('@/features/auth/ForgotPage'));
const ResetPage = lazy(() => import('@/features/auth/ResetPage'));
const FirstAccessPage = lazy(() => import('@/features/auth/FirstAccessPage'));
const SetupTwoFactorPage = lazy(() => import('@/features/auth/SetupTwoFactorPage'));
const NoAccessPage = lazy(() => import('@/features/auth/NoAccessPage'));

const ShellWithSheets = lazy(() => import('./ShellWithSheets'));

/** Sección con su guarda de rol y módulo. */
function section(path: SectionKey, Component: ComponentType): RouteObject {
  return {
    path: `${path}/*`,
    element: (
      <SectionGate section={path}>
        <Component />
      </SectionGate>
    ),
  };
}

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<BootScreen />}>{children}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <EntryRedirect /> },

      // Acceso (público)
      {
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'login/2fa', element: <TwoFactorPage /> },
          { path: 'recuperar', element: <ForgotPage /> },
          { path: 'restablecer/:token', element: <ResetPage /> },
        ],
      },
      // Alias de rutas viejas o cortas.
      { path: '2fa', element: <Navigate to="/login/2fa" replace /> },
      { path: 'cambiar-contrasena', element: <Navigate to="/primer-acceso" replace /> },

      // Sesión restringida (UX §3.5, §3.6) y sin acceso (§3.8)
      {
        path: 'primer-acceso',
        element: (
          <RequireSession allow="password">
            <AuthCard>
              <Page>
                <FirstAccessPage />
              </Page>
            </AuthCard>
          </RequireSession>
        ),
      },
      {
        path: 'configurar-2fa',
        element: (
          <RequireSession allow="mfaSetup">
            <AuthCard>
              <Page>
                <SetupTwoFactorPage />
              </Page>
            </AuthCard>
          </RequireSession>
        ),
      },
      {
        path: 'sin-acceso',
        element: (
          <RequireSession>
            <AuthCard logo="brand">
              <Page>
                <NoAccessPage />
              </Page>
            </AuthCard>
          </RequireSession>
        ),
      },

      // Primeros pasos (dueño, UX §3.9)
      {
        path: 'primeros-pasos/*',
        element: (
          <RequireSession>
            <OwnerSetupGate>
              <Page>
                <Onboarding />
              </Page>
            </OwnerSetupGate>
          </RequireSession>
        ),
      },

      // App
      {
        path: 'app',
        element: (
          <RequireSession>
            <Outlet />
          </RequireSession>
        ),
        children: [
          { index: true, element: <AppIndexRedirect /> },
          {
            path: ':locationSlug',
            element: (
              <RequireLocation>
                <Page>
                  <ShellWithSheets />
                </Page>
              </RequireLocation>
            ),
            children: [
              { index: true, element: <Navigate to="agenda" replace /> },
              section('agenda', Agenda),
              section('caja', Sales),
              section('clientes', Clients),
              section('gift-cards', GiftCards),
              section('stock', Stock),
              section('comisiones', Commissions),
              section('finanzas', Finance),
              section('tablero', Dashboard),
              { path: 'ajustes/mi-cuenta/*', element: <Account /> },
              {
                path: 'ajustes/*',
                element: (
                  <SettingsGate>
                    <Settings />
                  </SettingsGate>
                ),
              },
              section('ayuda', Help),
              { path: '*', element: <NotFoundInApp /> },
            ],
          },
        ],
      },

      // Página pública de reservas (chunk propio, sin sesión)
      {
        path: 'r/*',
        element: (
          <Page>
            <Booking />
          </Page>
        ),
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
